import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spin, Tooltip } from 'antd';
import type { NamePath } from '@xbcb/ui-types';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import { formatHTS } from '@xbcb/hts-client-utils';
import { clearFields } from 'libs/clearFields';
import { useArrivalDate, useHtsQuery, usePrevious } from 'libs/hooks';
import HTSNumberV2 from 'components/HTSNumberV2';
import { HtsObject } from 'types';
import { ModeOfTransport } from '__generated__/graphql';
import { useTariffEvaluation } from './useTariffEvaluation';
import { getRowLabel, formatDutyRate, computeDutyValue } from './utils';
import {
  ProductValueRow,
  TariffRow,
  TariffRowLabel,
  TariffRowContent,
  DutyInfo,
  RemoveButton,
} from './styles';

export interface LineItemTariffDisplayV2Props {
  tariffFullNamePath: NamePath;
  tariffLocalNamePath: NamePath;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  showValidateButton?: boolean;
  validateLoading?: boolean;
  onValidateHts?: () => void;
  lineIndex?: number;
  lineCount?: number;
}

const LINE_LEVEL_PRESERVE = [
  'tariffs',
  'manufacturer',
  'origin',
  'description',
  'poNumber',
  'externalReference',
  'censusWarnings',
];

export const LineItemTariffDisplayV2: React.FC<
  LineItemTariffDisplayV2Props
> = ({
  tariffFullNamePath,
  tariffLocalNamePath,
  required,
  disabled,
  readOnly,
  showValidateButton,
  validateLoading,
  onValidateHts,
}) => {
  const [bundle] = useBundle('components.LineItemTariffDisplayV2');
  const form = Form.useFormInstance();
  const arrivalDate = useArrivalDate();
  const { queryEffectiveUsHtss, getEffectiveUsHtssData } = useHtsQuery();
  const htsData = getEffectiveUsHtssData(arrivalDate);

  const tariffs = Form.useWatch(tariffFullNamePath, form);
  useEffect(() => {
    if (Array.isArray(tariffs) && tariffs.length === 0) {
      form.setFieldValue(tariffFullNamePath, [{}]);
    }
  }, [tariffs, form, tariffFullNamePath]);

  const lineFullNamePath = useMemo(
    () => (tariffFullNamePath as (string | number)[]).slice(0, -1),
    [tariffFullNamePath],
  );

  const linesKeyIdx = (tariffFullNamePath as (string | number)[]).lastIndexOf(
    'lines',
  );
  const productValuePath = useMemo(
    () =>
      linesKeyIdx >= 0
        ? [
            ...(tariffFullNamePath as (string | number)[]).slice(
              0,
              linesKeyIdx,
            ),
            'totalValue',
            'value',
          ]
        : [],
    [tariffFullNamePath, linesKeyIdx],
  );
  const rawProductValue = Form.useWatch(productValuePath, form);
  const productValue: number = Number(rawProductValue) || 0;

  const originNamePath = useMemo(
    () => [...lineFullNamePath, 'origin', 'countryCode'],
    [lineFullNamePath],
  );
  const originCountryCode: string | undefined = Form.useWatch(
    originNamePath,
    form,
  );

  const modeOfTransport: ModeOfTransport | undefined = Form.useWatch(
    ['conveyance', 'modeOfTransport'],
    form,
  );

  const { runTariffEvaluation, evaluating } = useTariffEvaluation({
    form,
    tariffFullNamePath,
    originCountryCode,
    arrivalDate,
    modeOfTransport,
  });
  const primaryTariffCode = tariffs?.[tariffs?.length - 1]?.htsNumber;

  const prevPrimaryTariffCode = usePrevious(primaryTariffCode);
  const prevOriginCountryCode = usePrevious(originCountryCode);
  const prevArrivalDate = usePrevious(arrivalDate);
  const prevModeOfTransport = usePrevious(modeOfTransport);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!primaryTariffCode || !originCountryCode) return;
    // Only re-evaluate when the HTS was already established in a prior render
    // and a shipping parameter changed. This prevents overwriting saved data on
    // initial load (where HTS and shipping params appear simultaneously).
    if (prevPrimaryTariffCode === undefined) return;
    const hasChanged =
      prevOriginCountryCode !== originCountryCode ||
      prevArrivalDate !== arrivalDate ||
      prevModeOfTransport !== modeOfTransport;
    if (!hasChanged) return;
    void runTariffEvaluation(primaryTariffCode);
  }, [
    primaryTariffCode,
    originCountryCode,
    arrivalDate,
    modeOfTransport,
    prevPrimaryTariffCode,
    prevArrivalDate,
    prevOriginCountryCode,
    prevModeOfTransport,
  ]);

  const [htsQueryLoading, setHtsQueryLoading] = useState(false);
  const loading = htsQueryLoading || evaluating;

  const handlePrimarySelect = useCallback(
    async (value: string) => {
      if (!value) return;
      form.setFieldValue(tariffFullNamePath, [{ htsNumber: value }]);
      clearFields({
        form,
        fullNamePath: lineFullNamePath,
        exclude: LINE_LEVEL_PRESERVE,
      })();
      setHtsQueryLoading(true);
      await queryEffectiveUsHtss([value], arrivalDate);
      setHtsQueryLoading(false);
      void runTariffEvaluation(value);
    },
    [
      form,
      tariffFullNamePath,
      lineFullNamePath,
      arrivalDate,
      queryEffectiveUsHtss,
      runTariffEvaluation,
    ],
  );

  const handleSecondarySelect = useCallback(
    (index: number) => async (value: string) => {
      if (!value) return;
      form.setFields([
        {
          name: [...tariffFullNamePath, index, 'tariffType'],
          value: undefined,
        },
        {
          name: [...tariffFullNamePath, index, 'additionalTariffType'],
          value: undefined,
        },
      ]);
      const formatted = formatHTS(value);
      if (!htsData[formatted]) {
        await queryEffectiveUsHtss([value], arrivalDate);
      }
    },
    [form, tariffFullNamePath, htsData, arrivalDate, queryEffectiveUsHtss],
  );

  const tariffsLength = tariffs?.length ?? 0;

  const handleClear = (index: number) => {
    const isPrimary = index === tariffsLength - 1;
    if (isPrimary) {
      form.setFieldValue(tariffFullNamePath, []);
      clearFields({
        form,
        fullNamePath: lineFullNamePath,
        exclude: LINE_LEVEL_PRESERVE,
      })();
    } else {
      form.setFields([
        { name: [...tariffFullNamePath, index, 'htsNumber'], value: undefined },
      ]);
    }
  };

  return (
    <Form.List name={tariffLocalNamePath} minimum={1}>
      {(fields, { add, remove }) => (
        <>
          {!readOnly &&
            primaryTariffCode &&
            htsData[formatHTS(primaryTariffCode)] && (
              <ProductValueRow>
                <Button size="small" onClick={() => add({}, 0)}>
                  {safeGetMessage(
                    bundle,
                    'add_duty_button',
                    undefined,
                    'Add duty',
                  )}
                </Button>
              </ProductValueRow>
            )}
          {fields.map((field, index) => {
            const allHtsNumbers: string[] = (tariffs ?? [])
              .map((tariff: any, tariffIndex: number) =>
                tariffIndex !== index ? tariff?.htsNumber : null,
              )
              .filter(Boolean);
            const tariffFormData = tariffs?.[index];
            const htsObj = tariffFormData?.htsNumber
              ? htsData[formatHTS(tariffFormData.htsNumber)]
              : undefined;
            const isPrimary = index === fields.length - 1;
            const formTariffType =
              tariffFormData?.tariffType ??
              tariffFormData?.additionalTariffType;
            const effectiveHtsObj: HtsObject | undefined = htsObj
              ? ({
                  ...htsObj,
                  additionalTariffType:
                    htsObj.additionalTariffType || formTariffType,
                } as HtsObject)
              : formTariffType
              ? ({
                  additionalTariffType: formTariffType,
                } as HtsObject)
              : undefined;
            const dutyRate = formatDutyRate(htsObj);
            const dutyValue = computeDutyValue(htsObj, productValue);
            return (
              <TariffRow key={field.key}>
                <Tooltip
                  title={htsObj?.fullText || htsObj?.text}
                  placement="topLeft"
                  mouseEnterDelay={0.5}
                >
                  <TariffRowLabel>
                    {getRowLabel(index, isPrimary, bundle, effectiveHtsObj)}
                  </TariffRowLabel>
                </Tooltip>
                <TariffRowContent>
                  <HTSNumberV2
                    field={[field.name, 'htsNumber']}
                    fullNamePath={[
                      ...tariffFullNamePath,
                      field.name,
                      'htsNumber',
                    ]}
                    required={required && isPrimary}
                    disabled={disabled}
                    readOnly={readOnly}
                    onSelect={
                      isPrimary
                        ? handlePrimarySelect
                        : handleSecondarySelect(index)
                    }
                    onClear={() => handleClear(index)}
                    excludeValues={allHtsNumbers}
                  />
                  <DutyInfo>
                    {dutyRate ? (
                      <span>
                        {safeGetMessage(
                          bundle,
                          'duty_rate',
                          { rate: dutyRate },
                          `Duty rate: ${dutyRate}`,
                        )}
                      </span>
                    ) : null}
                    {dutyValue ? (
                      <span>
                        {safeGetMessage(
                          bundle,
                          'duty_value',
                          { value: dutyValue },
                          `Duty value: ${dutyValue}`,
                        )}
                      </span>
                    ) : null}
                  </DutyInfo>
                  {!isPrimary && !readOnly && (
                    <RemoveButton
                      onClick={() => {
                        const current =
                          form.getFieldValue(tariffFullNamePath) || [];
                        form.setFieldValue(
                          tariffFullNamePath,
                          current.filter(
                            (_: unknown, i: number) => i !== index,
                          ),
                        );
                      }}
                    >
                      {safeGetMessage(
                        bundle,
                        'remove_button',
                        undefined,
                        'Remove',
                      )}
                    </RemoveButton>
                  )}
                  {isPrimary && showValidateButton && (
                    <Button onClick={onValidateHts} loading={validateLoading}>
                      {safeGetMessage(
                        bundle,
                        'validate_hts_button',
                        undefined,
                        'Validate HTS Number',
                      )}
                    </Button>
                  )}
                  {isPrimary && loading && <Spin size="small" />}
                </TariffRowContent>
              </TariffRow>
            );
          })}
        </>
      )}
    </Form.List>
  );
};

export default memo(LineItemTariffDisplayV2);
